/* eslint-disable @typescript-eslint/naming-convention */
import type { LeaderBoard } from '@ps/web-martech-cms-types';
import { RawButton, RawInput, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';
import countriesByCode from '@swf/generated/countries-by-code.json';

import { xmlToJson } from '../../utils/xml';

import {
  TXTCLI_Country,
  TXTCLI_No_data_available,
  TXTCLI_Place,
  TXTCLI_Player,
  TXTCLI_Points,
  TXTCLI_Search_for_players_by_their_Us_ELL,
  TXTCLI_Show_More_Options
} from './CMSLeaderBoard.messages.json';
import styles from './CMSLeaderBoard.scss';

interface Player {
  attributes: { rank: string; score: string; id: string; cc: string };
}

interface FeedXmlData {
  leagueInfo: {
    league: {
      standings: {
        player: Player[];
      };
    };
  };
}

interface FeedData {
  place: string;
  userId: string;
  country: string;
  points: string;
}

const AmountToShowOffest = 10;

const CMSLeaderBoard: React.FC<LeaderBoard> = ({ feed_options }) => {
  const [list, setList] = useState<FeedData[]>([]);
  const [filteredList, setFilteredList] = useState<FeedData[]>([]);
  const [amountToShow, setAmountToShow] = useState(AmountToShowOffest);
  const [searchValue, setSearchValue] = useState('');
  const [filter, setFilter] = useState(feed_options[0].xml_link);
  const [loading, setLoading] = useState(false);

  const { formatMessage } = useIntl();

  useEffect(() => {
    let result = [...list];

    if (searchValue.length) {
      result = result.filter((item) =>
        item.userId.toLowerCase().startsWith(searchValue.trim().toLowerCase())
      );
    }

    setAmountToShow(AmountToShowOffest);
    setFilteredList(result);
  }, [list, filter, searchValue]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchXML = async () => {
      setLoading(true);
      try {
        const feedDataResponse = await (
          await fetch(filter, {
            signal
          })
        ).text();
        const feedXml = new DOMParser().parseFromString(feedDataResponse, 'text/xml');
        const feedXmlData = xmlToJson(feedXml) as FeedXmlData;

        const feedData = feedXmlData.leagueInfo.league.standings.player.map((item) => ({
          place: item.attributes.rank,
          userId: item.attributes.id,
          country: item.attributes.cc,
          points: item.attributes.score
        })) as FeedData[];
        setList(feedData);
      } catch {
        setList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchXML();

    return () => abortController.abort();
  }, [filter]);

  return (
    <>
      <div className={styles.text}>
        <RawInput
          label=""
          value={searchValue}
          placeholder={`${formatMessage(TXTCLI_Search_for_players_by_their_Us_ELL)}`}
          id="player-name-filter"
          onChange={({ target }) => setSearchValue(target.value)}
        />
      </div>
      <div className={styles.tabsContainer}>
        {feed_options.map((item, i) => (
          <button
            className={classNames(styles.tabs, { [styles.active]: filter === item.xml_link })}
            key={i}
            onClick={() => setFilter(item.xml_link)}
          >
            {item.title}
          </button>
        ))}
      </div>
      {loading && (
        <div className={styles.loadingIndicator}>
          <LoadingIndicator loading={loading} />
        </div>
      )}
      {!loading && (
        <div className={styles.tableContainer}>
          <table className={styles.playersTable}>
            <thead>
              <tr>
                <th>
                  <Translation {...TXTCLI_Place} />
                </th>
                <th>
                  <Translation {...TXTCLI_Player} />
                </th>
                <th>
                  <Translation {...TXTCLI_Country} />
                </th>
                <th>
                  <Translation {...TXTCLI_Points} />
                </th>
              </tr>
            </thead>
            <tbody>
              {!filteredList.length && (
                <tr className={styles.noData}>
                  <td colSpan={4}>
                    <Translation {...TXTCLI_No_data_available} />
                  </td>
                </tr>
              )}
              {filteredList.slice(0, amountToShow).map((item, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td>{item.place}</td>
                    <td>{item.userId}</td>
                    <td>{countriesByCode[item.country.toUpperCase()]}</td>
                    <td>{item.points}</td>
                  </tr>
                </React.Fragment>
              ))}
              {filteredList.length >= amountToShow && (
                <tr className={styles.showMore}>
                  <td colSpan={4}>
                    <RawButton
                      variant="text"
                      onClick={(e: React.MouseEvent) =>
                        setAmountToShow((prev) => prev + AmountToShowOffest)
                      }
                    >
                      <Translation {...TXTCLI_Show_More_Options} />
                    </RawButton>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default CMSLeaderBoard;
